import {
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { useAddContactComment, useContactDetails } from '@/models/contacts';
import { ActivityFeed } from '@/components/ActivityFeed/ActivityFeed';
import { useHasAnyPermission } from '@/models/permissions';
import Link from '@/components/Link';
import CreateOfferModal from '@/pages/offers/CreateOfferModal';
import { CallsCard } from '@/components/CallsCard';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { buildContactFeed } from './feed';
import { IdentityCard } from './IdentityCard';
import { NotesCard } from './NotesCard';
import { ReferralCard } from './ReferralCard';
import { MembersControl } from './MembersControl';
import MondayContactKycPanel from './MondayContactKycPanel';

const MONDAY_PULSE_THRESHOLD = 1_000_000;

export default function ContactDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  if (id > MONDAY_PULSE_THRESHOLD) {
    return <MondayContactKycPanel pulseId={String(id)} />;
  }

  return <LocalContactDetail id={id} />;
}

function LocalContactDetail({ id }: { id: number }) {
  const { data, isLoading } = useContactDetails(id);
  const addComment = useAddContactComment();
  const [createOfferOpen, setCreateOfferOpen] = useState(false);
  const [, navigate] = useLocation();
  const canCreateOffer = useHasAnyPermission([
    'offers:write',
    'offers:*',
    '*:*',
  ]);
  const canAssign = useHasAnyPermission([
    'contacts:assign',
    'contacts:*',
    '*:*',
  ]);

  if (isLoading || !data) return null;
  const contact = data.contact;
  const contactLabel =
    [
      [contact.firstName, contact.lastName].filter(Boolean).join(' '),
      contact.email,
    ]
      .filter(Boolean)
      .join(' — ') || `#${contact.id}`;
  const fullName =
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—';

  return (
    <Paper p="md" h="100%">
      <Container size="xl" px={0}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              <Card withBorder>
                <Group justify="space-between">
                  <Stack gap={2}>
                    <Title order={3}>{fullName}</Title>
                    <Group gap="sm">
                      <MembersControl
                        contactId={id}
                        members={data.members}
                        readOnly={!canAssign}
                      />
                    </Group>
                  </Stack>
                </Group>
              </Card>

              <ActivityFeed
                items={buildContactFeed(data.events, data.comments)}
                onPostComment={(body) =>
                  addComment.mutateAsync({ contactId: id, body })
                }
                posting={addComment.isPending}
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md">
              <IdentityCard key={`identity-${contact.id}`} contact={contact} />
              <NotesCard key={`notes-${contact.id}`} contact={contact} />
              <ReferralCard key={`referral-${contact.id}`} contact={contact} />
              {data.account && (
                <CollapsibleCard title="Account">
                  <Link to={`~/accounts/${data.account.id}`}>
                    {data.account.name}
                  </Link>
                </CollapsibleCard>
              )}
              {data.sourceLead && (
                <CollapsibleCard title="From lead">
                  <Link to={`~/leads/${data.sourceLead.id}`}>
                    #{data.sourceLead.id}{' '}
                    {[data.sourceLead.firstName, data.sourceLead.lastName]
                      .filter(Boolean)
                      .join(' ') || ''}
                  </Link>
                  <Group gap="xs" mt={4}>
                    <Text size="xs" c="dimmed" span>
                      Original status:
                    </Text>
                    <Badge size="xs">{data.sourceLead.status}</Badge>
                  </Group>
                </CollapsibleCard>
              )}
              <CollapsibleCard
                title="Offers"
                rightSection={
                  <Group gap="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      {data.offers.length}
                    </Text>
                    {canCreateOffer && (
                      <Button
                        size="compact-xs"
                        onClick={() => setCreateOfferOpen(true)}
                      >
                        New offer
                      </Button>
                    )}
                  </Group>
                }
              >
                <Stack gap={4}>
                  {data.offers.length === 0 && (
                    <Text size="sm" c="dimmed">
                      No offers yet.
                    </Text>
                  )}
                  {data.offers.map((o) => (
                    <Group key={o.id} justify="space-between">
                      <Link to={`~/offers/${o.id}`}>{o.title}</Link>
                      <Text size="xs" c={o.accepted ? 'green' : 'dimmed'}>
                        {o.accepted ? 'accepted' : 'open'}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </CollapsibleCard>
              <CallsCard contactId={id} />

              <CreateOfferModal
                opened={createOfferOpen}
                onClose={() => setCreateOfferOpen(false)}
                initialContactId={contact.id}
                initialContactLabel={contactLabel}
                onCreated={(offer) => {
                  setCreateOfferOpen(false);
                  navigate(`~/offers/${offer.id}`);
                }}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
    </Paper>
  );
}
